import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MessageCircle, Coffee, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function ConnectionOutreachGuide() {
  const navigate = useNavigate();

  const messageTemplates = [
    {
      type: "Reconnection",
      example: "Hi [Name]! I was just thinking about our time at [company/project]. How have you been? I'd love to hear what you're up to these days."
    },
    {
      type: "Value-Add",
      example: "Hi [Name], I came across this article about [topic] and immediately thought of you given your work in [area]. Thought you might find it interesting!"
    },
    {
      type: "Coffee Request",
      example: "Hi [Name], I've always admired your career path in [field]. Would you have 20 minutes for a virtual coffee? I'd love to hear your perspective on [topic]."
    },
    {
      type: "Congratulations",
      example: "Hi [Name], I saw your recent [promotion/achievement/post]. Congratulations! That's such well-deserved recognition for your work."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Users className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Connection Outreach</h1>
          <p className="text-lg text-secondary-600">Nurture your professional network authentically</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Networking Matters</h2>
          <p className="text-secondary-700 mb-4">
            85% of jobs are filled through networking. But beyond job hunting, your network provides support, advice, referrals, and opportunities you'd never find alone. The best time to nurture relationships is before you need them.
          </p>
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <p className="text-orange-800 font-medium">
              💡 Networking isn't about using people—it's about building genuine relationships where both people can give and receive value.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-orange-500" />
            Message Templates
          </h2>
          <div className="space-y-4">
            {messageTemplates.map((template, i) => (
              <div key={i} className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-bold text-orange-800 mb-2">{template.type}</h3>
                <p className="text-secondary-700 italic text-sm">"{template.example}"</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-orange-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Mail className="w-6 h-6 text-orange-500" />
            Today's Challenge
          </h2>
          <p className="text-secondary-700 mb-4">Reach out to ONE person from one of these categories:</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-secondary-900 mb-2">Former Colleagues</h3>
              <p className="text-secondary-600 text-sm">People you've worked with and lost touch with</p>
            </div>
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-secondary-900 mb-2">Industry Contacts</h3>
              <p className="text-secondary-600 text-sm">People you've met at conferences or events</p>
            </div>
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-secondary-900 mb-2">Career Admiration</h3>
              <p className="text-secondary-600 text-sm">Someone whose career path inspires you</p>
            </div>
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-secondary-900 mb-2">Mentors/Advisors</h3>
              <p className="text-secondary-600 text-sm">Past mentors you haven't thanked recently</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Coffee className="w-6 h-6 text-amber-600" />
            Tips for Authentic Outreach
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-amber-500">✓</span>
              <span className="text-secondary-700">Be genuine—people can sense when you only want something</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500">✓</span>
              <span className="text-secondary-700">Lead with giving, not asking—share value first</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500">✓</span>
              <span className="text-secondary-700">Keep it brief—respect their time</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500">✓</span>
              <span className="text-secondary-700">Follow up—if they respond, continue the conversation</span>
            </li>
          </ul>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "Who did I reach out to today? How did it feel? Who else comes to mind that I should reconnect with?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default ConnectionOutreachGuide;
